import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Stack, Typography, Chip, IconButton, CircularProgress, Tooltip,
} from '@mui/material';
import { CheckCircle, WarningAmber, DeleteOutline } from '@mui/icons-material';
import { fetchBankAccounts, deleteBankAccount, fetchPayoutUPIMethods } from '../../store/slices/walletSlice';

const ProviderStatus = ({ registered }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
    {registered ? (
      <>
        <CheckCircle sx={{ fontSize: 14 }} color="success" />
        <Typography variant="caption" color="success.main">Ready for payouts</Typography>
      </>
    ) : (
      <>
        <WarningAmber sx={{ fontSize: 14 }} color="warning" />
        <Typography variant="caption" color="text.secondary">Not registered with payment provider</Typography>
      </>
    )}
  </Stack>
);

const methodItemSx = (isSelected, selectable) => ({
  p: 1.5,
  borderRadius: 2,
  border: '2px solid',
  borderColor: isSelected ? 'primary.main' : 'divider',
  bgcolor: isSelected ? 'action.selected' : 'background.paper',
  cursor: selectable ? 'pointer' : 'default',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  transition: 'border-color 0.15s ease',
});

const BankAccountList = ({ selectable, selectedId, onSelect }) => {
  const dispatch = useDispatch();
  const { bankAccounts, payoutUPIMethods, loading } = useSelector((s) => s.wallet);

  useEffect(() => {
    dispatch(fetchBankAccounts());
    dispatch(fetchPayoutUPIMethods());
  }, [dispatch]);

  const handleDelete = (id) => {
    if (window.confirm('Remove this bank account?')) {
      dispatch(deleteBankAccount(id));
    }
  };

  const isLoading = (loading.bankAccounts || loading.payoutUPI) &&
                    bankAccounts.length === 0 && payoutUPIMethods.length === 0;

  if (isLoading) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  const hasNoMethods = bankAccounts.length === 0 && payoutUPIMethods.length === 0;

  if (hasNoMethods) {
    return (
      <Typography variant="body2" color="text.secondary">
        No payout methods added yet. Add a bank account or UPI ID to receive payouts.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      {bankAccounts.map((acc) => (
        <Box
          key={`bank-${acc.id}`}
          onClick={() => selectable && onSelect && onSelect(acc.id, 'bank')}
          sx={methodItemSx(selectedId === acc.id, selectable)}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" fontWeight={600}>{acc.bank_name}</Typography>
              {acc.is_default && <Chip label="Default" size="small" color="success" sx={{ height: 20, fontSize: 10 }} />}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
              {acc.account_holder_name} &bull; {acc.account_number}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block">
              IFSC: {acc.ifsc}
            </Typography>
            <ProviderStatus registered={acc.provider_registered} />
          </Box>
          {!selectable && (
            <Tooltip title="Remove bank account">
              <IconButton size="small" color="error" onClick={() => handleDelete(acc.id)} aria-label="Remove bank account">
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}

      {payoutUPIMethods.map((upi) => (
        <Box
          key={`upi-${upi.id}`}
          onClick={() => selectable && onSelect && onSelect(upi.id, 'upi')}
          sx={methodItemSx(selectedId === `upi:${upi.id}`, selectable)}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="UPI" size="small" color="primary" sx={{ height: 20, fontSize: 10 }} aria-label="UPI payout method" />
              <Typography variant="subtitle2" fontWeight={600}>{upi.upi_id}</Typography>
            </Stack>
            {upi.upi_name && (
              <Typography variant="caption" color="text.secondary" display="block">
                Verified name: {upi.upi_name}
              </Typography>
            )}
            <ProviderStatus registered={upi.provider_registered} />
          </Box>
          {/* No Remove button for UPI -- delete is out of scope */}
        </Box>
      ))}
    </Stack>
  );
};

export default BankAccountList;
